/**
 * Report document schemas for the client SDK.
 *
 * Extends the SDK-agnostic base schemas from `../documents/report` with
 * `Timestamp`-transforming timestamp fields, and exposes typed refs used by the
 * webapp's Reports triage widget (global admin view + per-course TA-Home view).
 */
import type {
  CollectionReference,
  DocumentReference,
  Firestore,
  Query,
} from 'firebase/firestore';
import {z} from 'zod';
import {
  collection,
  collectionGroup,
  doc,
  getFirestore,
} from 'firebase/firestore';

import {
  ON_PLATFORM_REPORTS_SUBCOLLECTION,
  ReportDocumentSchema,
  ReportNoteDocumentSchema,
  ReportSeenDocumentSchema,
  getOnPlatformReportDocumentPath,
  getOnPlatformReportsCollectionPath,
  getReportNoteDocumentPath,
  getReportNotesCollectionPath,
  getReportSeenDocumentPath,
} from '../documents/report';
import {ClientTimestampSchema, createConverter} from './sdk';

// ============================================================================
// SDK-specific schemas (Timestamp transforms)
// ============================================================================

/**
 * Client-SDK report schema; timestamps become `Timestamp` instances.
 *
 * `updatedAt` is nullable here (unlike the always-set base schema) for the
 * same reason as {@link ReportSeenSchema.lastSeenAt} below: `ReportsSection`'s
 * status toggle and `NotesModal`'s note-count bump both write it with
 * `serverTimestamp()`, which resolves to `null` in the transient local
 * snapshot that fires before the server ack arrives.
 */
export const ReportSchema = ReportDocumentSchema.extend({
  lastNoteAt: ClientTimestampSchema.nullable(),
  createdAt: ClientTimestampSchema,
  updatedAt: ClientTimestampSchema.nullable(),
}).loose();

/** Inferred type for {@link ReportSchema}. */
export type Report = z.infer<typeof ReportSchema>;

/**
 * Client-SDK report-note schema.
 *
 * `at` is nullable here (unlike the always-set base schema) because
 * `NotesModal` writes it with `serverTimestamp()` — see
 * {@link ReportSeenSchema.lastSeenAt} for why the transient local snapshot
 * needs this.
 */
export const ReportNoteSchema = ReportNoteDocumentSchema.extend({
  at: ClientTimestampSchema.nullable(),
}).loose();

/** Inferred type for {@link ReportNoteSchema}. */
export type ReportNote = z.infer<typeof ReportNoteSchema>;

/**
 * Client-SDK per-viewer seen-marker schema.
 *
 * `lastSeenAt` is nullable here (unlike the always-set base schema) because
 * `NotesModal` writes it with `serverTimestamp()`, and the Firestore client
 * SDK's default snapshot behavior resolves an unacknowledged server-timestamp
 * sentinel to `null` in the *local* snapshot that fires immediately after the
 * optimistic write, before the server ack arrives. Without `.nullable()`
 * here, that transient local snapshot fails validation and throws.
 */
export const ReportSeenSchema = ReportSeenDocumentSchema.extend({
  lastSeenAt: ClientTimestampSchema.nullable(),
}).loose();

/** Inferred type for {@link ReportSeenSchema}. */
export type ReportSeen = z.infer<typeof ReportSeenSchema>;

// ============================================================================
// Converters
// ============================================================================

const reportConverter = createConverter({
  schema: ReportSchema,
  options: {validateOnRead: true, validateOnWrite: false},
});

const reportNoteConverter = createConverter({
  schema: ReportNoteSchema,
  options: {validateOnRead: true, validateOnWrite: false},
});

const reportSeenConverter = createConverter({
  schema: ReportSeenSchema,
  options: {validateOnRead: true, validateOnWrite: false},
});

// ============================================================================
// Document / collection refs
// ============================================================================

/**
 * Typed ref to a course bucket's on-platform reports subcollection. Apply
 * `query`/`where`/`orderBy` at the call site.
 *
 * @param args.courseId - The course bucket id.
 * @param args.firestore - Firestore instance; defaults to `getFirestore()`
 *   when omitted.
 * @returns Converter-bound collection ref for
 *   `reports/{courseId}/onPlatformReports`.
 */
export function getOnPlatformReportsCollectionRef({
  courseId,
  firestore = getFirestore(),
}: {
  courseId: string;
  firestore?: Firestore;
}): CollectionReference<Report> {
  return collection(
    firestore,
    getOnPlatformReportsCollectionPath({courseId}),
  ).withConverter(reportConverter);
}

/**
 * Typed collection-group query over every course's on-platform reports, for the
 * org-wide triage view. Apply `where`/`orderBy` at the call site.
 *
 * @param args.firestore - Firestore instance; defaults to `getFirestore()`
 *   when omitted.
 * @returns Converter-bound collection-group query for all `onPlatformReports`.
 */
export function getAllOnPlatformReportsGroupRef({
  firestore = getFirestore(),
}: {
  firestore?: Firestore;
} = {}): Query<Report> {
  return collectionGroup(
    firestore,
    ON_PLATFORM_REPORTS_SUBCOLLECTION,
  ).withConverter(reportConverter);
}

/**
 * Typed ref to a single on-platform report document.
 *
 * @param args.courseId - The course bucket id.
 * @param args.reportId - The report's id.
 * @param args.firestore - Firestore instance; defaults to `getFirestore()`
 *   when omitted.
 * @returns Converter-bound doc ref for
 *   `reports/{courseId}/onPlatformReports/{reportId}`.
 */
export function getOnPlatformReportDocRef({
  courseId,
  reportId,
  firestore = getFirestore(),
}: {
  courseId: string;
  reportId: string;
  firestore?: Firestore;
}): DocumentReference<Report> {
  return doc(
    firestore,
    getOnPlatformReportDocumentPath({courseId, reportId}),
  ).withConverter(reportConverter);
}

/**
 * Typed ref to a report's notes subcollection.
 *
 * @param args.courseId - The course bucket id.
 * @param args.reportId - The parent report's id.
 * @param args.firestore - Firestore instance; defaults to `getFirestore()`
 *   when omitted.
 * @returns Converter-bound collection ref for the report's `notes`.
 */
export function getReportNotesCollectionRef({
  courseId,
  reportId,
  firestore = getFirestore(),
}: {
  courseId: string;
  reportId: string;
  firestore?: Firestore;
}): CollectionReference<ReportNote> {
  return collection(
    firestore,
    getReportNotesCollectionPath({courseId, reportId}),
  ).withConverter(reportNoteConverter);
}

/**
 * Typed ref to a single report note.
 *
 * @param args.courseId - The course bucket id.
 * @param args.reportId - The parent report's id.
 * @param args.noteId - The note's id.
 * @param args.firestore - Firestore instance; defaults to `getFirestore()`
 *   when omitted.
 * @returns Converter-bound doc ref for the note.
 */
export function getReportNoteDocRef({
  courseId,
  reportId,
  noteId,
  firestore = getFirestore(),
}: {
  courseId: string;
  reportId: string;
  noteId: string;
  firestore?: Firestore;
}): DocumentReference<ReportNote> {
  return doc(
    firestore,
    getReportNoteDocumentPath({courseId, reportId, noteId}),
  ).withConverter(reportNoteConverter);
}

/**
 * Typed ref to the current viewer's seen marker for a report.
 *
 * @param args.courseId - The course bucket id.
 * @param args.reportId - The parent report's id.
 * @param args.uid - The viewer's uid (document id).
 * @param args.firestore - Firestore instance; defaults to `getFirestore()`
 *   when omitted.
 * @returns Converter-bound doc ref for the seen marker.
 */
export function getReportSeenDocRef({
  courseId,
  reportId,
  uid,
  firestore = getFirestore(),
}: {
  courseId: string;
  reportId: string;
  uid: string;
  firestore?: Firestore;
}): DocumentReference<ReportSeen> {
  return doc(
    firestore,
    getReportSeenDocumentPath({courseId, reportId, uid}),
  ).withConverter(reportSeenConverter);
}
